/** AfterMove Studio set. Original geometry authored for this project. */
export const palette = {
  ink: '#252923',
  ivory: '#f5f0df',
  brass: '#d8ad61',
  // Matches the locally stored Chess.com board PNG.
  sage: '#739552',
  parchment: '#ebecd0',
};
export const pieceTypes = ['P', 'N', 'B', 'R', 'Q', 'K'] as const;
export type PieceType = (typeof pieceTypes)[number];
export type PieceColor = 'w' | 'b';
export const pieceNames: Record<PieceType, string> = {
  P: 'Pawn',
  N: 'Knight',
  B: 'Bishop',
  R: 'Rook',
  Q: 'Queen',
  K: 'King',
};
// Clarity revision: neutral solid fills and a dark contour on both armies.
// Identity comes from the silhouette; shading never carries essential detail.
export const piecePalette = {
  white: '#faf9f6',
  black: '#292c31',
  outline: '#202329',
  whiteShade: '#cdd1d6',
  blackShade: '#454a52',
};
const stem = 'M36 49H60C58 59 60 65 68 73H28C36 65 38 59 36 49Z';
const collar = 'M31 47H65L63 54H33Z';
const foot = 'M28 73H68L75 80V86H21V80Z';
export function pieceSvg(type: PieceType, color: PieceColor): string {
  const light = color === 'w';
  const fill = light ? piecePalette.white : piecePalette.black;
  const shade = light ? piecePalette.whiteShade : piecePalette.blackShade;
  const detail = light ? piecePalette.outline : '#c3c7cd';
  const facet = (d: string) => `<path d="${d}" fill="${shade}" stroke="none"/>`;
  const line = (d: string, width = 2.5) =>
    `<path d="${d}" fill="none" stroke="${detail}" stroke-width="${width}"/>`;
  const shapes: Record<PieceType, string> = {
    P: `<path d="M39 47H57C55 57 56 64 63 73H33C40 64 41 57 39 47Z"/>${facet('M52 53H55C54 61 57 67 60 71H51Z')}<circle cx="48" cy="32" r="13"/>${facet('M54 22A12 12 0 0 1 42 43C52 44 58 33 54 22Z')}<path d="M35 46H61L59 52H37Z"/>`,
    N: `<path d="M26 73C26 61 33 53 44 46L34 49L23 44L24 37L35 26L38 14L47 21L54 12L58 23C70 28 76 42 71 61L68 73Z"/>${facet('M58 28C70 37 71 54 64 70H51C62 57 64 42 58 28Z')}${line('M29 42L35 43M41 28L43 24')}${line('M48 49C44 57 38 61 36 67')}<path d="M42 33L49 31" fill="none" stroke="${detail}" stroke-width="3.4"/>`,
    B: `<path d="${stem}"/>${facet('M54 54H58C58 62 61 67 65 71H54Z')}<path d="M48 14C42 21 30 29 30 38C30 47 38 51 48 51C58 51 66 47 66 38C66 29 54 21 48 14Z"/>${facet('M56 26C69 39 60 48 50 48C58 43 59 36 56 26Z')}${line('M54 24L43 38', 4.5)}<circle cx="48" cy="10" r="4"/><path d="${collar}"/>`,
    R: `<path d="M25 18H36V28H42V18H54V28H60V18H71V38L63 45V61L69 73H27L33 61V45L25 38Z"/>${facet('M55 44H61V62L66 71H55Z')}${line('M29 38H67M35 46H61M32 65H64')}`,
    Q: `<path d="${stem}"/>${facet('M54 54H58C58 62 61 67 65 71H54Z')}<path d="M29 47L19 25L34 34L35 17L43 32L48 11L53 32L61 17L62 34L77 25L67 47Z"/>${facet('M62 39L72 30L65 45H57Z')}<circle cx="19" cy="22" r="3.2"/><circle cx="35" cy="14" r="3.2"/><circle cx="48" cy="8" r="3.2"/><circle cx="61" cy="14" r="3.2"/><circle cx="77" cy="22" r="3.2"/><path d="${collar}"/>`,
    K: `<path d="${stem}"/>${facet('M54 54H58C58 62 61 67 65 71H54Z')}<path d="M33 48C26 43 24 34 30 30C36 26 43 30 48 36C53 30 60 26 66 30C72 34 70 43 63 48Z"/>${facet('M64 32C70 37 65 44 61 46H55C61 42 65 36 64 32Z')}<path d="M43 6H53V15H61V24H53V36H43V24H35V15H43Z"/><path d="${collar}"/>`,
  };
  const base = type === 'P' ? 'M33 73H63L70 80V86H26V80Z' : foot;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" fill="none"><title>${light ? 'White' : 'Black'} ${pieceNames[type]}</title><ellipse cx="48" cy="87" rx="${type === 'P' ? 24 : 29}" ry="3" fill="#202329" opacity=".16"/><g fill="${fill}" stroke="${piecePalette.outline}" stroke-width="2.8" stroke-linejoin="round" stroke-linecap="round">${shapes[type]}<path d="${base}"/>${facet(type === 'P' ? 'M62 76L67 81V83H29V81H62Z' : 'M67 76L72 81V83H24V81H67Z')}</g></svg>`;
}
/** A shared 24-unit grid, 1.6-unit stroke and rounded terminals. */
export const iconPaths = {
  arrow: ['M4 12H20M14 6L20 12L14 18'],
  rise: ['M6 18L18 6M7 6H18V17'],
  chevron: ['M7 10L12 15L17 10'],
  close: ['M6 6L18 18M18 6L6 18'],
  check: ['M5 12L10 17L20 6'],
  replay: ['M4 10A8 8 0 1 1 5 17M4 4V10H10'],
  rewind: ['M10 5L4 11L10 17M5 11H15a5 5 0 0 1 0 10h-3'],
  sound: ['M4 9H8L13 5V19L8 15H4ZM16 9C18 10.5 18 13.5 16 15M19 6C23 9 23 15 19 18'],
  muted: ['M4 9H8L13 5V19L8 15H4ZM17 9L22 15M22 9L17 15'],
  settings: [
    'M9 3H15L16 6L19 7L21 11V13L18 15L17 18L13 21H11L9 18L6 17L3 13V11L6 9L7 6ZM15 12A3 3 0 1 1 9 12A3 3 0 1 1 15 12',
  ],
  stats: ['M4 20H20M5 16V11H8V16M10.5 16V4H13.5V16M16 16V8H19V16'],
  flip: ['M7 4V20M3 8L7 4L11 8M17 4V20M13 16L17 20L21 16'],
  favorite: ['M12 3L14.8 8.7L21 9.6L16.5 14L17.6 20.2L12 17.3L6.4 20.2L7.5 14L3 9.6L9.2 8.7Z'],
  history: ['M4 9A8.2 8.2 0 1 1 4 16M4 4V9H9M12 7V12L15.5 14'],
  hint: ['M9 18H15M10 21H14M8 13C3 8 7 3 12 3C17 3 21 8 16 13L15 15H9ZM12 7V11'],
  external: ['M14 4H20V10M20 4L11 13M10 5H5V19H19V14'],
  bookmark: ['M7 3H17V21L12 17L7 21Z'],
  pen: ['M4 20L5 15L16 4L20 8L9 19Z', 'M13 7L17 11', 'M4 20L9 19'],
} as const;
export type IconName = keyof typeof iconPaths;
export function iconSvg(name: IconName): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${iconPaths[name].map((d) => `<path d="${d}"/>`).join('')}</svg>`;
}
export function brandMarkSvg(tile = false): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none"><title>AfterMove</title>${tile ? '<rect width="64" height="64" rx="15" fill="#252923"/>' : ''}<path d="M15 50C15 39 20 33 29 28L20 30C16 31 13 28 15 25L22 14L22 8L30 12L36 6L39 15C51 19 54 32 48 44H23" transform="translate(1 4)" fill="#d8ad61"/><path d="M30 23L34 21" stroke="#252923" stroke-width="2.8" stroke-linecap="round"/><path d="M15 54H47" stroke="#d8ad61" stroke-width="4" stroke-linecap="round"/><path d="M47 11H56V20" stroke="#d8ad61" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}
export function practiceMotifSvg(): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 180 180" fill="none"><g stroke="#d8ad61" stroke-width="1"><path d="M30 20V160M70 20V160M110 20V160M150 20V160M20 30H160M20 70H160M20 110H160M20 150H160" opacity=".35"/><rect x="31" y="111" width="38" height="38" rx="2" fill="#d8ad61" fill-opacity=".1"/><path d="M50 130V50H90M79 39L90 50L79 61" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/><circle cx="50" cy="130" r="5" fill="#d8ad61"/><rect x="71" y="31" width="38" height="38" rx="2" stroke-width="1.5"/></g></svg>`;
}
